import React, { useEffect, useState } from "react";
import { Modal, Button, Input, Radio, notification, Spin, Empty, Tooltip } from "antd";
import { CloseOutlined, PlusOutlined } from "@ant-design/icons";
import { cloneDeep, filter, find, forEach, get, includes, isEmpty, map } from "lodash";
import classnames from "classnames";
import { forkJoin } from "rxjs";
// adding local dependencies
import { ACTIVE, COMPLETE, Note } from "../../constants";
import { useCareCoordinationStore } from "@stores/useCareCoordinationStore";
import "./goal.scss";
// import InputField from "../input/input";
import Icon from "@assets/icon_config";
import Notes from "../notes/notes";
import CareCoordinationService from "@services/care-coordination";
import {
  CARE_MANAGEMENT_SCREEN_TITLE,
  CASE_STATUS_REASON_GOAL,
  MEMBER_DETAILS_CARE_MANAGEMENT_GOALS,
  USER_ACTION_CARD_PROGRAM,
  USER_ACTION_DESCRIPTION_GOAL_COMPLETE,
  USER_ACTION_DESCRIPTION_GOAL_DELETE,
  USER_ACTION_DESCRIPTION_GOAL_MARK,
  USER_ACTION_DESCRIPTION_GOAL_SAVE,
  USER_ACTION_DESCRIPTION_GOAL_UPDATE,
  USER_ACTION_LOCATION_GOAL,
  USER_ACTION_LOCATION_GOAL_NOTE,
  USER_ACTION_MODULE_MEMBER_DETAIL,
  USER_ACTION_TAB_CARE_MANAGEMENT,
  CASE_STATUS_REASON_NOTE,
  USER_ACTION_DESCRIPTION_NOTE_DELETE,
  USER_ACTION_LOCATION_TASK_NOTE,
} from "@utils/constants";
import HealthemSpinner from "@components/healthemSpinner/healthemSpinner";
import { CanEdit } from "@components/accessLevel";
import membersService from "@services/members.service";
import DataNotFound from "@components/noDataFound";
import { NO_DATA_FOUND } from "../../../../../constants/constants";

/******************************************************************************* */

// Code practice - while updating active program state of store, please update previous program state accordingly this is to handle and maintain history across intervention

/******************************************************************************* */

const Goal = (props: { goals: any; interventionId: any; programId: any; patientId: any }) => {
  // local states
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isGlobalGoalVisible, setIsGlobalGoalVisible] = useState(false);
  const [selectedValuesOfGlobalGoals, setSelectedValuesOfGlobalGoals] = useState();
  const [globalGoalsOptions, setGlobalGoalOptions] = useState<{ id: any; description: string }[] | never[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isGlobalLoading, setIsGlobalLoading] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState("");
  const [selectedGlobalGoalDescription, setSelectedGlobalGoalDescription] = useState(null);

  // getting state from the Store
  const setProgramsData = useCareCoordinationStore((state) => state.setDataForCareCoordination);
  const previousDataForCareCoordination = cloneDeep(useCareCoordinationStore((state) => state.previousDataForCareCoordination));
  const setPreviousDataForCareCoordination = useCareCoordinationStore((state) => state.setPreviousDataForCareCoordination);
  const programsData = cloneDeep(useCareCoordinationStore((state) => state.dataForCareCoordination));
  const patientId = props.patientId;

  // finding out active program and active intervention
  const activeProgram = programsData;
  const activeIntervention = find(activeProgram?.interventions, { id: props?.interventionId });

  // finding out active program's and active intervention's previous state
  const activeProgramPreviousState = previousDataForCareCoordination;
  const activeInterventionPreviousState = find(activeProgramPreviousState?.interventions, { id: props?.interventionId });

  // Loading global notes
  useEffect(() => {
    getGlobalGoals();
  }, []);

  // params for update status for Program goals
  const currentMember = membersService?.getCurrentMember;
  const getUserActionParams = (userActionDescription: string, reason?: string, isGoalNoteDelete?: boolean) => {
    let props = {
      caseStatusReasonTxt: isGoalNoteDelete ? CASE_STATUS_REASON_NOTE : reason,
      actionModule: USER_ACTION_MODULE_MEMBER_DETAIL,
      actionTab: USER_ACTION_TAB_CARE_MANAGEMENT,
      actionCard: `${get(programsData, "displayName")}$|${get(programsData, "patientProgramId")}$|${USER_ACTION_CARD_PROGRAM}`,
      actionLocation: isGoalNoteDelete ? USER_ACTION_LOCATION_GOAL_NOTE : USER_ACTION_LOCATION_GOAL,
      actionDescription: userActionDescription,
    };
    return props;
  };
  // function responsible to show the task's goal modal
  const showModal = () => {
    setIsModalVisible(true);
  };

  // function responsible to hide the task's goal modal
  const handleCancel = () => {
    const canClose = filter(activeIntervention?.goals, (goal) => {
      return isEmpty(goal.description);
    });

    isEmpty(canClose)
      ? setIsModalVisible(false)
      : notification.error({
          message: "Error",
          description: 
          <div>
            <div>
              Already active or empty goals exists!
            </div>
            <div>
              Provide descriptions or remove them.
            </div>
          </div>,
        });
  };

  // function responsible to show the global goal options modal
  const showGlobalGoalModal = () => {
    setIsGlobalGoalVisible(true);
  };

  const getGlobalGoals = (callBack?: any, goalDescription?: any) => {
    setIsGlobalLoading(true);
    CareCoordinationService.fetchGlobalGoalList({ patientId: patientId }).subscribe({
      next: (v) => {
        setGlobalGoalOptions(v?.data?.data?.goals);
        setIsGlobalLoading(false);
        if (callBack) {
          callBack(v?.data?.data?.goals, goalDescription);
        }
      },
      error: (e) => {
        console.log(e);
      },
    });
  };

  // function responsible to hide the global goal options modal
  const handleGlobalPopUpCancel = () => {
    setIsGlobalGoalVisible(false);
    setSelectedGlobalGoalDescription(null);
    setSearchValue("");
  };

  // function responsible to set the state of selected global goals
  const handleSelectingGlobalGoal = (checkedValues: any) => {
    setSelectedValuesOfGlobalGoals(checkedValues);
    const globalGoal: any = find(globalGoalsOptions, { id: checkedValues });
    setSelectedGlobalGoalDescription(globalGoal?.description);
  };

  // adding new goal
  const addNewGoal = () => {
    setIsLoading(true);
    CareCoordinationService.createNewGoal({ patientId: patientId, interventionId: props?.interventionId, description: "" }).subscribe({
      next: (v) => {
        const response = v?.data;

        console.log(response);

        const newGoal = {
          id: response?.data?.goalId,
          goal_status: ACTIVE,
          notes: [],
          description: "",
          isGlobal: "N",
        };
        if (!activeIntervention?.goals) {
          activeIntervention.goals = [];
          activeInterventionPreviousState.goals = [];
        }
        activeIntervention.goals.push(newGoal);
        activeInterventionPreviousState.goals.push(newGoal);

        setProgramsData(programsData);
        setPreviousDataForCareCoordination(previousDataForCareCoordination);
        setIsLoading(false);
      },
      error: (e) => {
        console.log(e);
      },
    });
    const { caseStatusReasonTxt, actionModule, actionTab, actionCard, actionLocation, actionDescription } = getUserActionParams(USER_ACTION_DESCRIPTION_GOAL_SAVE, CASE_STATUS_REASON_GOAL);
    membersService.updateStatus(currentMember?.patient_id, caseStatusReasonTxt, actionModule, actionTab, actionCard, actionLocation, actionDescription).subscribe({
      next: (v) => {
        console.log("updated status successfully");
      },
      error: (e) => {
        console.log("update status failed");
      },
    });
  };

  // adding global goals
  const addGlobalGoals = () => {
    const description = get(find(globalGoalsOptions, { id: selectedValuesOfGlobalGoals }), "description", null);
    setIsLoading(true);
    CareCoordinationService.createNewGoal({
      patientId: patientId,
      interventionId: props?.interventionId,
      description: description,
      isGlobal: "Y",
    }).subscribe({
      next: (v) => {
        const response = v.data.data;

        const newGoals = {
          id: response.goalId,
          goal_status: ACTIVE,
          notes: [],
          description: description,
          isGlobal: "Y",
        };

        if (isEmpty(activeIntervention.goals)) {
          activeIntervention.goals = [newGoals];
          activeInterventionPreviousState.goals = [newGoals];
        } else {
          activeIntervention.goals.push(newGoals);
          activeInterventionPreviousState.goals.push(newGoals);
        }

        setProgramsData(programsData);
        setPreviousDataForCareCoordination(previousDataForCareCoordination);
        setIsGlobalGoalVisible(false);
        setIsLoading(false);
        setSelectedGlobalGoalDescription(null);
        setSearchValue("");
      },
      error: (e) => {
        console.log(e);
        setIsGlobalGoalVisible(false);
        setIsLoading(false);
        setSelectedGlobalGoalDescription(null);
        setSearchValue("");
      },
    });
    const { caseStatusReasonTxt, actionModule, actionTab, actionCard, actionLocation, actionDescription } = getUserActionParams(USER_ACTION_DESCRIPTION_GOAL_SAVE, CASE_STATUS_REASON_GOAL);
    membersService.updateStatus(currentMember?.patient_id, caseStatusReasonTxt, actionModule, actionTab, actionCard, actionLocation, actionDescription).subscribe({
      next: (v) => {
        console.log("updated status successfully");
      },
      error: (e) => {
        console.log("update status failed");
      },
    });
  };

  // updating the goal
  const updateGoal = ({ status, goalId, description, globalFlag, isTemplate }: any) => {
    setIsLoading(true);
    CareCoordinationService.updateGoal({
      patientId: patientId,
      interventionId: props?.interventionId,
      goalId: goalId,
      description: description,
      status: status,
      globalFlag: globalFlag,
      isTemplate: isTemplate,
    }).subscribe({
      next: (v: any) => {
        const response = v;

        console.log(response);

        const activeGoal = find(activeIntervention?.goals, { id: goalId });
        activeGoal.goal_status = status;
        activeGoal.description = description;
        activeGoal.isGlobal = globalFlag;

        const activeGoalPreviousState = find(activeInterventionPreviousState?.goals, { id: goalId });
        activeGoalPreviousState.goal_status = status;
        activeGoalPreviousState.description = description;
        activeGoalPreviousState.isGlobal = globalFlag;

        setProgramsData(programsData);
        setPreviousDataForCareCoordination(previousDataForCareCoordination);
        setIsLoading(false);
      },
      error: (e: any) => {
        console.log(e);
        setIsLoading(false);
      },
    });
  };

  // Deleting the goal
  const deleteGoal = ({ goalId }: any) => {
    setIsLoading(true);
    const goalNotes = (find(activeIntervention.goals, { id: goalId })?.notes || []).map((note: Note) => note.notesId);
    CareCoordinationService.deleteGoal({ patientId: patientId, interventionId: props?.interventionId, goalId: goalId, goalNotes }).subscribe({
      next: (v) => {
        activeIntervention.goals = filter(activeIntervention?.goals, (goal) => goal.id !== goalId);
        activeInterventionPreviousState.goals = filter(activeInterventionPreviousState?.goals, (goal) => goal.id !== goalId);

        setProgramsData(programsData);
        setPreviousDataForCareCoordination(previousDataForCareCoordination);
        setIsLoading(false);
      },
      error: (e) => {
        console.log(e);
        setIsLoading(false);
      },
    });

    const goalDeletionActionParams = getUserActionParams(USER_ACTION_DESCRIPTION_GOAL_DELETE, CASE_STATUS_REASON_GOAL);

    const goalNotesDeletionActionParams = getUserActionParams(USER_ACTION_DESCRIPTION_NOTE_DELETE, CASE_STATUS_REASON_NOTE);

    const promises: any[] = [];
    promises.push(
      membersService.updateStatus(
        currentMember?.patient_id,
        goalDeletionActionParams.caseStatusReasonTxt,
        goalDeletionActionParams.actionModule,
        goalDeletionActionParams.actionTab,
        goalDeletionActionParams.actionCard,
        goalDeletionActionParams.actionLocation,
        goalDeletionActionParams.actionDescription
      )
    );
    if (goalNotes.length > 0) {
      promises.push(
        membersService.updateStatus(
          currentMember?.patient_id,
          goalNotesDeletionActionParams.caseStatusReasonTxt,
          goalNotesDeletionActionParams.actionModule,
          goalNotesDeletionActionParams.actionTab,
          goalNotesDeletionActionParams.actionCard,
          goalNotesDeletionActionParams.actionLocation,
          goalNotesDeletionActionParams.actionDescription
        )
      );
    }
    forkJoin(...promises).subscribe({
      next: (v) => {
        console.log("updated status successfully");
      },
      error: (e) => {
        console.log("update status failed");
      },
    });
  };

  // modifying global goal options by excluding the already selected options
  const modifiedGlobalGoalOptions = () => {
    if (!isEmpty(searchValue)) {
      const options = filter(globalGoalsOptions, (goal) => {
        // console.log(goal.description, searchValue, includes(goal.description, searchValue));
        return includes(goal?.description?.toLowerCase(), searchValue?.toLowerCase());
      });
      return options;
    } else {
      return globalGoalsOptions;
    }
  };

  const duplicateGoalErrors = (goalDescription: any) => {
    const selectedGoalIdArray = filter(props.goals, (goal) => {
      return goalDescription?.trim()?.toLocaleLowerCase() === goal?.description?.trim()?.toLocaleLowerCase() ? goal?.goal_status === "Active" : false;
    });
    if (selectedGoalIdArray?.length || !goalDescription?.length) {
      return true;
    }
  };

  const completeAllGoals = () => {
    setIsLoading(true);
    const activeGoalIds: any[] = [];
    forEach(activeIntervention?.goals, (goal) => {
      if (goal.goal_status !== COMPLETE) {
        activeGoalIds.push(goal?.id);
      }
    });

    CareCoordinationService.completeAllGoals({ patientId: patientId, interventionId: props?.interventionId, goalIds: activeGoalIds }).subscribe({
      next: (v) => {
        const response = v;

        console.log(response);

        forEach(activeIntervention?.goals, (goal) => {
          goal.goal_status = COMPLETE;
        });

        activeInterventionPreviousState.goals = activeIntervention?.goals;

        setProgramsData(programsData);
        setPreviousDataForCareCoordination(previousDataForCareCoordination);
        setIsLoading(false);
      },
      error: (e) => {
        console.log(e);
        setIsLoading(false);
      },
    });
    const { caseStatusReasonTxt, actionModule, actionTab, actionCard, actionLocation, actionDescription } = getUserActionParams(USER_ACTION_DESCRIPTION_GOAL_COMPLETE, CASE_STATUS_REASON_GOAL);
    membersService.updateStatus(currentMember?.patient_id, caseStatusReasonTxt, actionModule, actionTab, actionCard, actionLocation, actionDescription).subscribe({
      next: (v) => {
        console.log("updated status successfully");
      },
      error: (e) => {
        console.log("update status failed");
      },
    });
  };

  return (
    <div className="ui-program-intervention-goals__goal-modal">
      <Button
        className="ui-program-interventions__task__actions__action"
        icon={<Icon name="goals" colorOfBrand={!isEmpty(props?.goals) ? "#d15f25" : "#B3BAC5"} />}
        title="Add Goal"
        onClick={showModal}
        type="text"
      />

      {/* Task Goals */}

      <Modal
        title={
          <div className="ui-program-intervention-goals__modal-header">
            <Icon name="goals" colorOfBrand="#42526E" />
            <div className="ui-program-intervention-goals__modal-header__heading">Goals</div>
            {/* complete all goals Button */}
            <CanEdit>
              {!isEmpty(props?.goals) &&
                activeIntervention?.status === ACTIVE &&
                !isEmpty(
                  filter(activeIntervention?.goals, (goal) => {
                    return goal?.goal_status !== COMPLETE;
                  })
                ) && (
                  <Button
                    className="ui-program-intervention-goals__modal-header__complete-all-goals"
                    disabled={
                      !isEmpty(
                        filter(activeIntervention?.goals, (goal) => {
                          return isEmpty(goal.description);
                        })
                      )
                    }
                    onClick={() => completeAllGoals()}
                    type="primary"
                  >
                    Complete All Goals
                  </Button>
                )}
            </CanEdit>
          </div>
        }
        visible={isModalVisible}
        footer={[]}
        width={1000}
        centered
        closeIcon={<CloseOutlined onClick={handleCancel} style={{ paddingTop: "1.5rem" }} />}
      >
        <Spin spinning={isLoading} indicator={<HealthemSpinner size={40} />}>
          <div className="ui-program-intervention-goals__goal-modal">
            <div className={`ui-program-intervention-goals__goal-modal__goal-wrapper ${props.goals.length > 3 ? "goal-container custom-scroll-bar" : ""}`}>
              {/* drawing each goal separately */}
              {map(props.goals, (goal, index) => {
                return (
                  <InterventionGoal
                    goal={goal}
                    index={index}
                    updateGoal={updateGoal}
                    getUserActionParams={getUserActionParams}
                    deleteGoal={deleteGoal}
                    patientId={patientId}
                    duplicateGoalErrors={duplicateGoalErrors}
                    getGlobalGoals={getGlobalGoals}
                  />
                );
              })}
              {!(props.goals.length > 0) && get(activeIntervention, "status") === COMPLETE ? <DataNotFound message={NO_DATA_FOUND} /> : null}
            </div>
            <CanEdit>
              {get(activeIntervention, "status", ACTIVE) === ACTIVE && (
                <div className="ui-program-intervention-goals__goal-modal__goal-wrapper__actions">
                  <div
                    className="ui-program-intervention-goals__goal-modal__goal-wrapper__actions__action"
                    onClick={() => {
                      addNewGoal();
                    }}
                  >
                    <PlusOutlined color="#0072e3" /> New Goal
                  </div>
                  <div
                    className="ui-program-intervention-goals__goal-modal__goal-wrapper__actions__action"
                    onClick={() => {
                      getGlobalGoals();
                      showGlobalGoalModal();
                      setSelectedValuesOfGlobalGoals(undefined);
                    }}
                  >
                    <PlusOutlined color="#0072e3" /> Search and Add Global Goal
                  </div>
                </div>
              )}
            </CanEdit>
          </div>
        </Spin>
      </Modal>

      {/* Global Goals Modal */}
      <Modal
        className="ui-program-intervention-goals__global-goals"
        title="Global Goals"
        visible={isGlobalGoalVisible}
        onCancel={handleGlobalPopUpCancel}
        footer={[
          <div className="ui-program-intervention-goals__global-goals__footer">
            <div className="ui-program-intervention-goals__global-goals__footer__message">
              <div className="ui-program-intervention-goals__global-goals__error">
                {selectedGlobalGoalDescription && duplicateGoalErrors(selectedGlobalGoalDescription) ? `* Active goal already exists` : null}
              </div>
            </div>
            <div className="ui-program-intervention-goals__global-goals__footer__action-button">
              <Button key="back" onClick={handleGlobalPopUpCancel}>
                Cancel
              </Button>
              <Button key="submit" type="primary" onClick={addGlobalGoals} disabled={!selectedValuesOfGlobalGoals || duplicateGoalErrors(selectedGlobalGoalDescription)}>
                Save
              </Button>
            </div>
          </div>,
        ]}
      >
        <Spin spinning={isGlobalLoading} indicator={<HealthemSpinner size={40} />}>
          <Input
            className="ui-program-intervention-goals__global-goals__search"
            value={searchValue}
            onChange={(e) => {
              setSearchValue(e.target.value);
              setSelectedValuesOfGlobalGoals(undefined);
            }}
            placeholder="Search"
            suffix={<Icon name="search" />}
          />
          <Radio.Group
            value={selectedValuesOfGlobalGoals}
            className="ui-program-intervention-goals__global-goals__options custom-scroll-bar"
            onChange={({ target: { value } }) => handleSelectingGlobalGoal(value)}
          >
            {map(modifiedGlobalGoalOptions(), (goal, index) => {
              const optionClassName = classnames("ui-program-intervention-goals__global-goals__options__option", {
                "ui-program-intervention-goals__global-goals__options__option--selected": selectedValuesOfGlobalGoals === goal?.id,
              });
              return (
                <Radio.Button className={optionClassName} key={goal?.id + index} value={goal?.id}>
                  <Tooltip
                    placement="right"
                    title={goal?.description && goal?.description?.length > 50 ? goal?.description : null}
                    overlayClassName={"ui-program-intervention-goals__global-goals__options__option__tooltip"}
                  >
                    <span className="ui-program-intervention-goals__global-goals__options__option__description">{goal?.description}</span>
                  </Tooltip>
                </Radio.Button>
              );
            })}
          </Radio.Group>
        </Spin>
      </Modal>
    </div>
  );
};

const InterventionGoal = ({ goal, index, updateGoal, getUserActionParams, deleteGoal, patientId, duplicateGoalErrors, getGlobalGoals }: any) => {
  const currentMember = membersService?.getCurrentMember;

  const goalId = get(goal, "id", null);
  const goalStatus = get(goal, "goal_status", ACTIVE);
  const goalDescription = get(goal, "description", null);
  const goalIsGlobal = get(goal, "isGlobal", "N");
  const noteType = { goalId: goalId, noteType: MEMBER_DETAILS_CARE_MANAGEMENT_GOALS };

  const [expandObject, setexpandObject] = useState<any>({ id: null, isExpanded: false });
  const [error, setError] = useState(goalDescription?.trim().length > 0 ? false : true);

  const _className = classnames("ui-program-intervention-goals__goal-modal__goal-wrapper__goal__header__text-section__complete-action", {
    "ui-program-intervention-goals__goal-modal__goal-wrapper__goal__header__text-section__complete-action--disabled": goalStatus === COMPLETE || isEmpty(goalDescription),
  });

  const toggleExpandTestArea = (barrierId: any) => {
    if (expandObject.id && expandObject.id !== barrierId) {
      setexpandObject({
        id: barrierId,
        isExpanded: true,
      });
    } else if (expandObject.id !== barrierId) {
      setexpandObject({
        id: barrierId,
        isExpanded: true,
      });
    } else {
      setexpandObject({
        id: null,
        isExpanded: false,
      });
    }
  };

  const globalizeGoal = (globalGoalList: any) => {
    const existingGoal: any = find(globalGoalList, function (tempGoal: any) {
      return tempGoal?.description?.trim()?.toLocaleLowerCase() === goalDescription?.trim()?.toLocaleLowerCase();
    });

    if (existingGoal) {
      notification.error({
        message: "Error",
        description: "The global goal with description already exists. Cannot create duplicate global goals.",
      });
    } else {
      updateGoal({ goalId: goalId, globalFlag: "Y", isTemplate: "Y", description: goalDescription, status: goalStatus });
      const { caseStatusReasonTxt, actionModule, actionTab, actionCard, actionLocation, actionDescription } = getUserActionParams(USER_ACTION_DESCRIPTION_GOAL_MARK, CASE_STATUS_REASON_GOAL);
      membersService.updateStatus(currentMember?.patient_id, caseStatusReasonTxt, actionModule, actionTab, actionCard, actionLocation, actionDescription).subscribe({
        next: (v) => {
          console.log("updated status successfully");
        },
        error: (e) => {
          console.log("update status failed");
        },
      });
    }
  };

  const checkForGlobalizeGoal = () => {
    getGlobalGoals(globalizeGoal);
  };

  const updateDescription = (globalGoalList: any, goalDescription?: any) => {
    const existingGoal: any = find(globalGoalList, function (tempGoal: any) {
      return tempGoal?.description?.trim()?.toLocaleLowerCase() === goalDescription?.trim()?.toLocaleLowerCase();
    });
    updateGoal({ description: goalDescription, goalId: goalId, status: goalStatus, globalFlag: existingGoal ? "Y" : goalIsGlobal });
    const { caseStatusReasonTxt, actionModule, actionTab, actionCard, actionLocation, actionDescription } = getUserActionParams(USER_ACTION_DESCRIPTION_GOAL_UPDATE, CASE_STATUS_REASON_GOAL);
    membersService.updateStatus(currentMember?.patient_id, caseStatusReasonTxt, actionModule, actionTab, actionCard, actionLocation, actionDescription).subscribe({
      next: (v) => {
        console.log("updated status successfully");
      },
      error: (e) => {
        console.log("update status failed");
      },
    });
  };

  const checkForGlobalGoalAndUpdate = (goalDescription: any) => {
    getGlobalGoals(updateDescription, goalDescription);
  };

  return (
    <div
      className={`ui-program-intervention-goals__goal-modal__goal-wrapper__goal textAreaTransistion`}
      key={goalId + index}
      style={{ height: expandObject.id && expandObject.id === goalId ? "303px" : "103px" }}
    >
      <div className="ui-program-intervention-goals__goal-modal__goal-wrapper__goal__header">
        <div className="ui-program-intervention-goals__goal-modal__goal-wrapper__goal__header__text-section">
          {/* Complete and Active Action */}
          <CanEdit hide={false}>
            <div
              className={_className}
              title="Complete Goal"
              onClick={() => {
                if (goalStatus === COMPLETE || isEmpty(goalDescription)) {
                  return false;
                }
                updateGoal({ status: COMPLETE, goalId: goalId, description: goalDescription, globalFlag: goalIsGlobal });
                const { caseStatusReasonTxt, actionModule, actionTab, actionCard, actionLocation, actionDescription } = getUserActionParams(
                  USER_ACTION_DESCRIPTION_GOAL_COMPLETE,
                  CASE_STATUS_REASON_GOAL
                );
                membersService.updateStatus(currentMember?.patient_id, caseStatusReasonTxt, actionModule, actionTab, actionCard, actionLocation, actionDescription).subscribe({
                  next: (v) => {
                    console.log("updated status successfully");
                  },
                  error: (e) => {
                    console.log("update status failed");
                  },
                });
              }}
            >
              {goalStatus === ACTIVE ? <Icon name="tick-empty" /> : <Icon name="tick-filled" />}
            </div>
          </CanEdit>

          {/* Description of the goal */}
          <CanEdit hide={false}>
            <Input.TextArea
              onChange={(event: any) => {
                setError(duplicateGoalErrors(event.target.value));
              }}
              className="ui-program-intervention-goals__goal-modal__goal-wrapper__goal__header__text-section__textArea"
              defaultValue={goalDescription}
              disabled={goalStatus === COMPLETE || goalIsGlobal === "Y"}
              onBlur={(value) => {
                if (!error && value.target.value?.length) {
                  checkForGlobalGoalAndUpdate(value.target.value);
                }
              }}
              autoSize={{ minRows: 1, maxRows: expandObject.id && expandObject.id === goalId ? 10 : 2 }}
              placeholder="Enter Description"
            />
          </CanEdit>
        </div>
        <div className="ui-program-intervention-goals__goal-modal__goal-wrapper__goal__header__error-message">{error ? `* Active goal already exists or description cannot be empty.` : null}</div>
      </div>
      <div className="ui-program-intervention-goals__goal-modal__goal-wrapper__goal__actions">
        {/* Note   */}
        <div style={{ display: "flex" }}>
          <Notes
            masterData={goal}
            screen={CARE_MANAGEMENT_SCREEN_TITLE}
            page={USER_ACTION_LOCATION_GOAL_NOTE}
            noteType={noteType}
            patientId={patientId}
            disabled={goalStatus === COMPLETE || error ? true : false}
          />

          {/* Is Global */}
          <CanEdit hide={false}>
            <Button
              onClick={checkForGlobalizeGoal}
              className="ui-program-intervention-goals__goal-modal__goal-wrapper__goal__actions__action"
              icon={<Icon name="globe" colorOfBrand={goalIsGlobal === "Y" ? "#d15f25" : "#7a869a"} />}
              title="Mark/Un-Mark Global Goal"
              type="text"
              disabled={isEmpty(goalDescription) || goalIsGlobal === "Y" || goalStatus === COMPLETE || error}
            />
          </CanEdit>

          {/* Delete Action */}
          <CanEdit>
            <Button
              disabled={goalStatus === COMPLETE}
              className="ui-program-intervention-goals__goal-modal__goal-wrapper__goal__actions__action"
              icon={<Icon name="delete" colorOfBrand="#7A869A" />}
              title="Delete the Goal"
              onClick={() => {
                deleteGoal({ goalId: goalId });
              }}
              type="text"
            />
          </CanEdit>
        </div>
        <div className="barriers_expand_text_area">
          <Button
            onClick={() => toggleExpandTestArea(goalId)}
            className="ui-program-intervention-task-barriers__barrier-modal__barrier-wrapper__barrier__actions__action expandIcon"
            icon={<Icon name={expandObject.id && expandObject.id === goalId ? `contract` : `expand`} />}
            title="Expand"
            type="text"
          />
        </div>
      </div>
    </div>
  );
};

export default Goal;
